import type { ReactiveController, ReactiveControllerHost } from 'lit';

interface TriggerControllerOptions {
    /** Returns the current CSS selector for the trigger element. Read at host connect time. */
    getSelector: () => string | undefined;
    /** Called when the trigger is clicked. */
    onActivate: () => void;
}

/**
 * Manages an external "trigger" element on behalf of a dialog-like component:
 * binds a click listener, sets ARIA attributes (`aria-haspopup="dialog"`,
 * `aria-expanded`), and provides a focus restoration helper.
 *
 * Binding is one-shot at host connect time — the trigger element must exist
 * in the DOM by then.
 */
export class TriggerController implements ReactiveController {
    private _options: TriggerControllerOptions;
    private _triggerEl: HTMLElement | null = null;

    constructor(host: ReactiveControllerHost, options: TriggerControllerOptions) {
        this._options = options;
        host.addController(this);
    }

    /** Updates `aria-expanded` on the trigger to reflect the host's open state. */
    setExpanded(open: boolean): void {
        this._triggerEl?.setAttribute('aria-expanded', String(open));
    }

    /** Restores focus to the trigger element, if any. */
    focus(): void {
        this._triggerEl?.focus();
    }

    hostConnected(): void {
        const selector = this._options.getSelector();
        if (!selector) return;
        this._triggerEl = document.querySelector<HTMLElement>(selector);
        if (!this._triggerEl) return;
        this._triggerEl.addEventListener('click', this._onClick);
        this._triggerEl.setAttribute('aria-haspopup', 'dialog');
        this._triggerEl.setAttribute('aria-expanded', 'false');
    }

    hostDisconnected(): void {
        if (!this._triggerEl) return;
        this._triggerEl.removeEventListener('click', this._onClick);
        this._triggerEl.removeAttribute('aria-haspopup');
        this._triggerEl.removeAttribute('aria-expanded');
        this._triggerEl = null;
    }

    private _onClick = (): void => {
        this._options.onActivate();
    };
}
